import React, {
  Children,
  cloneElement,
  ReactNode,
  useEffect,
  useState,
  createContext,
  ReactElement,
  useContext,
  useRef,
} from "react";
import { useTransitionState } from "react-transition-state";
import clsx from "clsx";

const FadeUpContext = createContext({
  initialized: false,
  finishedAnimatingChildren: false,
});

const AnimateChild = ({
  isActive,
  child,
  isExiting,
}: {
  isActive: boolean;
  child: ReactElement<any>;
  isExiting: boolean;
}) => {
  const [{ status, isMounted }, toggle] = useTransitionState({
    timeout: 500, // Longer timeout for more visible animation
    preEnter: true,
    mountOnEnter: true,
    unmountOnExit: true,
  });

  useEffect(() => {
    toggle(isActive && !isExiting);
  }, [isActive, isExiting, toggle]);

  const hasBackdropFilter =
    typeof CSS !== "undefined" ? CSS.supports("backdrop-filter", "blur(1px)") : false;

  if (!isMounted) return null;

  const hasGlass = child.props.className?.includes("glass");

  return cloneElement(child, {
    className: clsx(
      "!transition-all !duration-500 !ease-in-out w-full",
      {
        // Entrance styles
        "!opacity-0 !translate-y-10 !scale-95":
          (status === "preEnter" || status === "exiting") && !isExiting,
        "!opacity-100 !translate-y-0 !scale-100": status === "entered" && !isExiting,

        // Exit styles (more dramatic)
        "!opacity-0 !translate-y-10 !scale-75 !blur-sm": isExiting && status === "exiting",

        // Glass override
        "!translate-y-0 !opacity-100": hasGlass && hasBackdropFilter && !isExiting,
      },
      child.props.className
    ),
  });
};

const FadeUp = ({ children }: { children: ReactNode }) => {
  const arrayChildren = Children.toArray(children) as ReactElement[];
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isExiting, setIsExiting] = useState(false);
  const prevChildrenRef = useRef(arrayChildren);
  const { initialized, finishedAnimatingChildren } = useContext(FadeUpContext);

  // Handle exit sequence when children are removed
  useEffect(() => {
    if (arrayChildren.length < prevChildrenRef.current.length) {
      setIsExiting(true);
      setActiveIndex(arrayChildren.length - 1);
    }
    prevChildrenRef.current = arrayChildren;
  }, [arrayChildren.length]);

  // Entrance animation logic
  useEffect(() => {
    if (isExiting) return;

    const timer = setTimeout(() => {
      if (activeIndex < arrayChildren.length - 1) {
        setActiveIndex(prev => prev + 1);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [activeIndex, arrayChildren.length, isExiting]);

  // Exit animation logic
  useEffect(() => {
    if (!isExiting) return;

    const timer = setTimeout(() => {
      if (activeIndex >= 0) {
        setActiveIndex(prev => prev - 1);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [activeIndex, isExiting]);

  return (
    <FadeUpContext.Provider
      value={{
        initialized: true,
        finishedAnimatingChildren: activeIndex >= arrayChildren.length - 1,
      }}
    >
      {arrayChildren.map((child, i) => (
        <AnimateChild
          key={child.key || i}
          isActive={i <= activeIndex}
          child={child}
          isExiting={isExiting}
        />
      ))}
    </FadeUpContext.Provider>
  );
};

export default FadeUp;
